import Modal from '../Modal';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import { useModal } from '../../../providers/ModalProvider';
import { modals } from '../../../providers/modals';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import { Text } from '../../typography';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import { useQueryParams } from '../../../hooks/misc';
import useCheckPermission from '../../../providers/UsePermissionHook';
import { actions } from '../../../utilities';
import styles from './FileMoveModal.module.scss';
import classNames from 'classnames';
import SimpleWorkspaceCard from '../../SimpleWorkspaceCard';
import { Form, Spinner } from 'react-bootstrap';
import Button from '../../Button';

const directoryQuery = loader('./FileMoveModal.fetch.graphql');

export default function FileMoveModal() {
  const { t } = useTranslation();
  const [newParent, setNewParent] = useState(null);
  const [searchString, setSearchString] = useState(null);
  const { modalState, closeModal } = useModal();
  const { show, onSubmit } = modalState.fileMoveModal;
  const { workspace, allWorkspaces, inWorkspaces } = useWorkspace();
  const { getParam } = useQueryParams();
  const tab = getParam('tab') || 'global';
  const hasViewAllPermission = useCheckPermission({
    action: actions.viewAll,
    model: 'file',
  });

  const workspaceIdFilter = useMemo(() => {
    let operator = 'eq';
    let value = `${workspace?.id}`;
    if (!!allWorkspaces && tab !== 'global') {
      operator = hasViewAllPermission ? 'not' : 'in';
      value = hasViewAllPermission
        ? `null`
        : inWorkspaces.map(({ id }) => id.toString());
    }
    if (tab !== 'workspace') {
      value = `null`;
    }
    return { field: 'workspaceId', operator, value };
  }, [allWorkspaces, tab, workspace?.id, hasViewAllPermission, inWorkspaces]);

  const onHide = () => {
    closeModal({ modalName: modals.fileMove });
    setNewParent(null);
    setSearchString(null);
  };

  const { loading, data: { files: searchResults = [] } = {} } = useQuery(
    directoryQuery,
    {
      skip: !show || !searchString,
      variables: {
        options: {
          sort: [{ field: 'name', order: 'desc' }],
          filters: [
            { field: 'isCertification', operator: 'eq', value: `false` },
            {
              field: 'vector',
              operator: 'eq',
              value: tab === 'ai' ? 'true' : `false`,
            },
            {
              field: 'expenseId',
              operator: 'eq',
              value: 'null',
            },
            {
              field: 'conditionalActionId',
              operator: 'eq',
              value: 'null',
            },
            workspaceIdFilter,
            {
              field: 'name',
              operator: 'iLike',
              value: [`%${searchString}%`],
            },
            { field: 'url', operator: 'eq', value: 'null' },
          ],
        },
      },
    },
  );

  return (
    <Modal
      title={`${t(keys.files.MOVE)} ${t(keys.common.FILE)}`}
      open={show}
      onSubmit={() =>
        onSubmit({
          parentId: newParent?.id,
          workspaceId: newParent?.workspaceId,
        })
      }
      onCancel={onHide}
      onClose={onHide}
      submitText={t(keys.files.MOVE)}
      submitDisabled={!newParent}
    >
      <Text marginXs weight="semiBold" color="secondary">
        {t(keys.files.SEARCH_DIRECTORIES)}
      </Text>
      <div className={classNames(styles.row, styles.gapSm)}>
        <Form.Control
          type="text"
          defaultValue={null}
          onBlur={({ target: { value } }) => {
            setSearchString(value);
          }}
          placeholder={t(keys.files.SEARCH_DIRECTORIES_PLACEHOLDER)}
        />
        <Button
          size="sm"
          value={t(keys.action.SEARCH, { variable: null })}
          icon="search"
          onClick={() => {}}
        />
      </div>
      <div className={styles.spacerSm} />
      <div className={styles.list}>
        {loading ? (
          <Spinner />
        ) : searchResults?.length > 0 ? (
          <div>
            <div
              className={classNames(
                styles.row,
                styles.spaceBetween,
                styles.card,
              )}
            >
              <Text noMargin weight="semiBold" color="secondary">
                {t(keys.common.NAME)}
              </Text>
              <Text noMargin weight="semiBold" color="secondary">
                {t(keys.common.WORKSPACE)}
              </Text>
            </div>
            {searchResults.map((dir) => (
              <div
                onClick={() =>
                  setNewParent((prev) => (prev?.id === dir?.id ? null : dir))
                }
                className={classNames(
                  styles.row,
                  styles.spaceBetween,
                  styles.card,
                  newParent?.id === dir.id ? styles.selected : {},
                )}
              >
                <Text weight="semiBold" noMargin className={styles.halfWidth}>
                  {dir?.name}
                </Text>
                <SimpleWorkspaceCard
                  workspace={allWorkspaces?.find(
                    (w) => w?.id === dir.workspaceId,
                  )}
                />
              </div>
            ))}
          </div>
        ) : searchString ? (
          <Text
            weight="semiBold"
            noMargin
            className={styles.fullWidth}
            color="secondary"
          >
            {t(keys.common.NONE)}
          </Text>
        ) : null}
      </div>
    </Modal>
  );
}
